<script lang="ts">
export default {
  name: 'PromptManager',
};
</script>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from 'reka-ui';
import PromptPlaygroundPage from './PromptPlaygroundPage.vue';
import PromptMappingPage from './PromptMappingPage.vue';
import PromptManagerPage from './PromptManagerPage.vue';
import ResultsPage from './ResultsPage.vue';
import MonitoringPage from './MonitoringPage.vue';

const route = useRoute();
const router = useRouter();

// Valid tab values
const validTabs = ['playground', 'mapping', 'prompts', 'results', 'monitoring'];

// Initialize currentTab from URL parameter or default to 'playground'
const getInitialTab = (): string => {
  const tabParam = route.query.tab as string;
  return validTabs.includes(tabParam) ? tabParam : 'playground';
};

const currentTab = ref<string>(getInitialTab());

// Params that belong to specific tabs (cleaned when switching away)
const tabParams: Record<string, string[]> = {
  prompts: ['prompt', 'version'],
  results: ['resultId', 'status', 'accountSearch', 'failureId'],
  playground: ['documentId', 'accountId', 's3Key', 'documentName', 'promptId', 'promptVersion', 'modelArn'],
};

// Watch for tab changes and update URL, cleaning up stale params from previous tab
watch(currentTab, (newTab, oldTab) => {
  if (route.query.tab !== newTab) {
    const query = { ...route.query, tab: newTab };

    // Remove params that belong to the previous tab
    if (oldTab && tabParams[oldTab]) {
      for (const param of tabParams[oldTab]) {
        delete query[param];
      }
    }

    router.replace({ query });
  }
});

// Watch for URL parameter changes and update current tab
watch(
  () => route.query.tab,
  (newTab) => {
    if (typeof newTab === 'string' && validTabs.includes(newTab) && newTab !== currentTab.value) {
      currentTab.value = newTab;
    }
  },
);

// Set initial tab from URL on mount
onMounted(() => {
  const tabFromUrl = route.query.tab as string;
  if (tabFromUrl && validTabs.includes(tabFromUrl)) {
    currentTab.value = tabFromUrl;
  }
});
</script>

<template>
  <div class="min-h-full bg-linear-to-br from-slate-50 to-blue-50 p-6">
    <div class="mx-auto max-w-7/8 2xl:max-w-6/8 space-y-6">
      <!-- Navigation Tabs -->
      <TabsRoot
        v-model="currentTab"
        class="w-full">
        <TabsList class="inline-flex h-10 items-center justify-center rounded-md bg-slate-100 p-1 text-slate-600">
          <TabsTrigger
            value="playground"
            class="inline-flex items-center justify-center rounded-sm px-3 py-1.5 text-sm font-medium whitespace-nowrap ring-offset-white transition-all focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white data-[state=active]:text-slate-950 data-[state=active]:shadow-xs">
            Playground
          </TabsTrigger>
          <TabsTrigger
            value="mapping"
            class="inline-flex items-center justify-center rounded-sm px-3 py-1.5 text-sm font-medium whitespace-nowrap ring-offset-white transition-all focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white data-[state=active]:text-slate-950 data-[state=active]:shadow-xs">
            Mapping
          </TabsTrigger>
          <TabsTrigger
            value="prompts"
            class="inline-flex items-center justify-center rounded-sm px-3 py-1.5 text-sm font-medium whitespace-nowrap ring-offset-white transition-all focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white data-[state=active]:text-slate-950 data-[state=active]:shadow-xs">
            Prompts
          </TabsTrigger>
          <TabsTrigger
            value="results"
            class="inline-flex items-center justify-center rounded-sm px-3 py-1.5 text-sm font-medium whitespace-nowrap ring-offset-white transition-all focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white data-[state=active]:text-slate-950 data-[state=active]:shadow-xs">
            Extraction Runs
          </TabsTrigger>
          <TabsTrigger
            value="monitoring"
            class="inline-flex items-center justify-center rounded-sm px-3 py-1.5 text-sm font-medium whitespace-nowrap ring-offset-white transition-all focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white data-[state=active]:text-slate-950 data-[state=active]:shadow-xs">
            Monitoring
          </TabsTrigger>
        </TabsList>

        <!-- Playground Tab Content -->
        <TabsContent
          value="playground"
          class="mt-4">
          <PromptPlaygroundPage />
        </TabsContent>

        <!-- Mapping Tab Content -->
        <TabsContent
          value="mapping"
          class="mt-4">
          <PromptMappingPage />
        </TabsContent>

        <!-- Prompts Tab Content -->
        <TabsContent
          value="prompts"
          class="mt-4">
          <PromptManagerPage />
        </TabsContent>

        <!-- Results Tab Content -->
        <TabsContent
          value="results"
          class="mt-4">
          <ResultsPage />
        </TabsContent>

        <!-- Monitoring Tab Content -->
        <TabsContent
          value="monitoring"
          class="mt-4">
          <MonitoringPage />
        </TabsContent>
      </TabsRoot>
    </div>
  </div>
</template>
